/**
 * Scroll positions of a page, so a tab comes back where you left it.
 * The page is not kept mounted, so each scrolled box is found again by its
 * place in the page (child indexes from the main area).
 */
export type WorkspaceScrollSnapshot = Array<{
  path: number[];
  top: number;
  left: number;
}>;

const scrollRoot = () =>
  document.getElementById("main-content") ?? document.querySelector("main");

const pathFrom = (root: Element, element: Element) => {
  const path: number[] = [];
  let node: Element | null = element;
  while (node && node !== root) {
    const parent: Element | null = node.parentElement;
    if (!parent) return null;
    path.unshift(Array.prototype.indexOf.call(parent.children, node));
    node = parent;
  }
  return node === root ? path : null;
};

const elementAt = (root: Element, path: number[]) => {
  let node: Element | undefined = root;
  for (const index of path) {
    node = node?.children[index];
    if (!node) return null;
  }
  return node ?? null;
};

export const captureWorkspaceScroll = (): WorkspaceScrollSnapshot => {
  const root = scrollRoot();
  if (!root) return [];
  const snapshot: WorkspaceScrollSnapshot = [];
  for (const element of [root, ...root.querySelectorAll("*")]) {
    if (element.scrollTop < 1 && element.scrollLeft < 1) continue;
    const path = pathFrom(root, element);
    if (path) {
      snapshot.push({ path, top: element.scrollTop, left: element.scrollLeft });
    }
  }
  return snapshot;
};

/**
 * Put the boxes back as their content loads (up to 2.5 s). Stops at once
 * if you scroll, type, or click, so it never fights you.
 */
export const restoreWorkspaceScroll = (snapshot: WorkspaceScrollSnapshot) => {
  if (!snapshot.length) return () => undefined;
  const pending = [...snapshot];
  const startedAt = performance.now();
  let frame = 0;
  let stopped = false;

  const stop = () => {
    stopped = true;
    cancelAnimationFrame(frame);
    for (const type of ["wheel", "touchstart", "keydown", "pointerdown"]) {
      window.removeEventListener(type, stop, true);
    }
  };
  for (const type of ["wheel", "touchstart", "keydown", "pointerdown"]) {
    window.addEventListener(type, stop, { capture: true, passive: true });
  }

  const tick = () => {
    if (stopped) return;
    const root = scrollRoot();
    if (root) {
      for (let i = pending.length - 1; i >= 0; i--) {
        const entry = pending[i];
        const element = elementAt(root, entry.path);
        if (!element) continue;
        const room = element.scrollHeight - element.clientHeight;
        if (room + 1 < entry.top) continue; // Content still loading.
        element.scrollTop = entry.top;
        element.scrollLeft = entry.left;
        pending.splice(i, 1);
      }
    }
    if (!pending.length || performance.now() - startedAt > 2500) {
      stop();
      return;
    }
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);
  return stop;
};
